import { Button, Chip, cn, DismissControl, Picker } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronDown, ChevronRight, Eye, EyeOff, FileDiff, Loader2, ShieldCheck, Sparkles, Undo2 } from 'lucide-react'
import * as React from 'react'
import { PacketText } from '../../components/packet-text'
import { formatIsoTimestampsInText, formatWhen } from '../../lib/display-time'
import { CardPathChip, CaseLinksPopover, DIY_KIND, DiyBody, DiyDoneForm, diyPayload } from '../case-links'
import { caseApi } from '../case-view/api'
import { addBusinessDays, fromLocalInput, toLocalInput } from '../next-move/due'
import { REPORT_KIND, ReportCardActions, ReportCardBody } from '../report-card/report-card'
import {
  ASYNC_CARD_KINDS,
  isReplyCard,
  type RileyCard,
  type RileyCardPreviewDiff,
  type RileyCardProgress,
  type RileyFeedbackTarget,
} from './contracts'
import { RileyFeedback } from './feedback'

/** A confirm click stops spinning after this and shows the error with Retry (#567). */
export const CONFIRM_WAIT_MS = 30_000
export const CONFIRM_TIMEOUT_MESSAGE = 'Confirm did not finish. Nothing may have been sent. Retry.'

function confirmFailure(reason: unknown): string {
  const name = typeof reason === 'object' && reason !== null && 'name' in reason ? String(reason.name) : ''
  const message = typeof reason === 'object' && reason !== null && 'message' in reason ? String(reason.message) : ''
  // happy-dom's DOMException is not an Error, so a timed-out confirm must be recognized by name.
  if (name === 'TimeoutError' || message === 'timeout' || message === CONFIRM_TIMEOUT_MESSAGE) return CONFIRM_TIMEOUT_MESSAGE
  if (message) return message
  return 'The card could not be updated.'
}

function withDeadline<T>(work: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new DOMException(CONFIRM_TIMEOUT_MESSAGE, 'TimeoutError')), ms)
    work.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

const STATE_LABEL: Record<RileyCard['state'], string> = {
  proposed: 'Awaiting decision',
  confirmed: 'Done',
  edited_confirmed: 'Edited + done',
  dismissed: 'Dismissed',
  expired: 'Expired',
  failed: 'Failed',
}

const IMPACT_LABEL: Record<RileyCard['audience'], string> = {
  customer: 'What the customer will see',
  contractor: 'What the contractor will see',
  vendor: 'What the contractor will see',
  internal: 'What staff will see',
  unclassified: 'Who will see this is not classified',
}

function scrubSummary(report?: Record<string, number> | null): string | null {
  if (!report) return null
  const counts = Object.entries(report)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([kind, count]) => `${count} ${kind.replaceAll('_', ' ')}`)
  return counts.length ? `Scrub report: ${counts.join(' · ')}` : null
}

function CloseoutSystemPicker({ card, value, onChange }: { card: RileyCard; value: string; onChange: (systemId: string | null) => void }) {
  const systems = useQuery({
    queryKey: ['case', card.case_id, 'phone-systems', 'closeout'],
    queryFn: () => caseApi().phoneSystems(String(card.case_id)),
    enabled: Boolean(card.case_id),
    retry: false,
  })
  const onlyId = systems.data?.length === 1 ? systems.data[0]?.id : undefined
  React.useEffect(() => {
    if (onlyId && !value) onChange(onlyId)
  }, [onChange, onlyId, value])
  return (
    <div className="mt-3 grid gap-1 text-xs font-medium">
      <span>Conduit phone system for service history</span>
      <Picker
        ariaLabel="Close-out phone system"
        value={value}
        disabled={systems.isPending || systems.isError}
        placeholder="Select a system"
        clearable
        onValueChange={(next) => onChange(next || null)}
        options={(systems.data ?? []).map((system) => ({ value: system.id, label: system.name, description: system.site ?? undefined }))}
      />
      {systems.data?.length === 1 ? <span className="text-primary">The customer's only system was selected.</span> : null}
      {systems.isError ? <span className="text-warning">Systems are unreachable; close-out remains blocked, not guessed.</span> : null}
    </div>
  )
}

type CloseoutHardware = {
  item_id: string
  selected?: boolean
  action?: string
  name?: string | null
  removed_name?: string | null
  serial_number?: string | null
}

function closeoutHardware(payload: Record<string, unknown>): CloseoutHardware[] {
  if (!Array.isArray(payload.hardware_changes)) return []
  return payload.hardware_changes.flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return []
    const item = value as Partial<CloseoutHardware>
    return typeof item.item_id === 'string' ? [item as CloseoutHardware] : []
  })
}

function closeoutHistory(payload: Record<string, unknown>): string {
  const history = payload.service_history
  return history && typeof history === 'object' && !Array.isArray(history) && typeof (history as { summary?: unknown }).summary === 'string'
    ? String((history as { summary: string }).summary)
    : ''
}

function CloseoutEditForm({
  card,
  busy,
  onConfirm,
  onCancel,
  onTidy,
}: {
  card: RileyCard
  busy: boolean
  onConfirm: (edit: Record<string, unknown>) => void
  onCancel: () => void
  onTidy?: (card: RileyCard, text: string) => Promise<{ text: string; stripped?: string[] }>
}) {
  const hardware = React.useMemo(() => closeoutHardware(card.payload), [card.payload])
  const [phoneSystemId, setPhoneSystemId] = React.useState(() =>
    typeof card.payload.phone_system_id === 'string' ? card.payload.phone_system_id : '',
  )
  const [history, setHistory] = React.useState(() => closeoutHistory(card.payload))
  const [selected, setSelected] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(hardware.map((item) => [item.item_id, item.selected !== false])),
  )
  const [tidying, setTidying] = React.useState(false)
  const [tidyNote, setTidyNote] = React.useState<string | null>(null)
  const [tidyError, setTidyError] = React.useState<string | null>(null)

  const tidy = async () => {
    if (!onTidy || !history.trim() || tidying) return
    setTidying(true)
    setTidyError(null)
    try {
      const result = await onTidy(card, history.trim())
      setHistory(result.text)
      setTidyNote(
        result.stripped?.length ? `Customer-safe scrub removed: ${result.stripped.join(', ')}` : 'Riley draft loaded for your review.',
      )
    } catch (reason) {
      setTidyError(reason instanceof Error ? reason.message : 'Riley could not tidy this draft.')
    } finally {
      setTidying(false)
    }
  }

  return (
    <form
      className="mt-3 space-y-4 rounded-md border border-border bg-muted/30 p-3"
      aria-label="Edit Conduit close-out"
      onSubmit={(event) => {
        event.preventDefault()
        if (!history.trim()) return
        onConfirm({
          phone_system_id: phoneSystemId || null,
          hardware_selected: selected,
          service_history_text: history.trim(),
        })
      }}
    >
      <div>
        <label className="mb-1 block text-xs font-medium" htmlFor={`service-history-${card.id}`}>
          Service history
        </label>
        <textarea
          id={`service-history-${card.id}`}
          value={history}
          maxLength={700}
          onChange={(event) => setHistory(event.target.value)}
          className="min-h-28 w-full rounded-md border border-border-strong bg-canvas p-2 text-sm outline-none focus:border-primary"
        />
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {onTidy ? (
            <Button type="button" size="sm" disabled={busy || !history.trim()} pending={tidying} onClick={() => void tidy()}>
              <Sparkles /> Tidy with Riley
            </Button>
          ) : null}
          <span className="text-xs text-muted-foreground">Draft only. Riley sends and writes nothing.</span>
        </div>
        {tidyNote ? (
          <p role="status" className="mt-1 text-xs text-success">
            {tidyNote}
          </p>
        ) : null}
        {tidyError ? (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {tidyError}
          </p>
        ) : null}
      </div>

      {hardware.length ? (
        <fieldset className="space-y-2">
          <legend className="text-xs font-medium">Hardware changes</legend>
          {hardware.map((item) => {
            const label = item.name || item.removed_name || item.item_id
            const detail = [item.action, item.serial_number].filter(Boolean).join(' · ')
            return (
              <label key={item.item_id} className="flex items-start gap-2 rounded-md border border-border bg-canvas px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-primary"
                  checked={selected[item.item_id] ?? false}
                  onChange={(event) => setSelected((current) => ({ ...current, [item.item_id]: event.target.checked }))}
                />
                <span>
                  <span className="block font-medium">{label}</span>
                  {detail ? <span className="block text-xs text-muted-foreground">{detail}</span> : null}
                </span>
              </label>
            )
          })}
        </fieldset>
      ) : null}

      <CloseoutSystemPicker card={card} value={phoneSystemId} onChange={(value) => setPhoneSystemId(value ?? '')} />

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" size="sm" disabled={busy || !history.trim()}>
          Confirm edit
        </Button>
        <Button type="button" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function initialNextCheck(card: RileyCard): string {
  const raw = card.payload.next_check_at
  if (typeof raw === 'string' && raw) {
    const parsed = new Date(raw)
    if (!Number.isNaN(parsed.getTime())) return toLocalInput(parsed)
  }
  return toLocalInput(addBusinessDays(new Date(), 1))
}

function FollowUpEditForm({
  card,
  busy,
  onConfirm,
  onCancel,
}: {
  card: RileyCard
  busy: boolean
  onConfirm: (edit: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const [message, setMessage] = React.useState(() => (typeof card.payload.message === 'string' ? card.payload.message : ''))
  const [nextCheck, setNextCheck] = React.useState(() => initialNextCheck(card))
  const nextCheckAt = fromLocalInput(nextCheck)
  return (
    <form
      className="mt-3 space-y-3 rounded-md border border-border bg-muted/30 p-3"
      aria-label="Edit owner follow-up"
      onSubmit={(event) => {
        event.preventDefault()
        if (message.trim() && nextCheckAt) onConfirm({ message: message.trim(), next_check_at: nextCheckAt })
      }}
    >
      <label className="block space-y-1 text-xs font-medium" htmlFor={`owner-follow-up-${card.id}`}>
        Private follow-up
        <textarea
          id={`owner-follow-up-${card.id}`}
          value={message}
          maxLength={10_000}
          onChange={(event) => setMessage(event.target.value)}
          className="min-h-28 w-full rounded-md border border-border-strong bg-canvas p-2 text-sm font-normal outline-none focus:border-primary"
        />
      </label>
      <label className="block space-y-1 text-xs font-medium" htmlFor={`owner-follow-up-check-${card.id}`}>
        Next check
        <input
          id={`owner-follow-up-check-${card.id}`}
          type="datetime-local"
          value={nextCheck}
          onChange={(event) => setNextCheck(event.target.value)}
          className="w-full rounded-md border border-border-strong bg-canvas p-2 text-sm font-normal outline-none focus:border-primary"
        />
      </label>
      <p className="text-xs text-muted-foreground">
        The next check can change. The owner, move, channel and Internal audience stay frozen.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" size="sm" disabled={busy || !message.trim() || !nextCheckAt}>
          Confirm edit
        </Button>
        <Button type="button" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

/** While editing, the preview follows the edited payload, so the person confirms what will actually run (#67). */
function EditedPreview({ raw }: { raw: string }) {
  const preview = editedPreviewOf(raw)
  if (!preview.ok) return <p className="mt-2 text-sm text-destructive">The edited payload is not a JSON object yet.</p>
  return (
    <div className="mt-2 min-w-0 max-w-full space-y-2 overflow-hidden text-sm text-foreground/85" data-slot="edited-preview">
      <p className="text-xs font-medium text-muted-foreground">After your edit (the server re-checks it before anything runs)</p>
      {preview.rows.length ? (
        <dl className="grid min-w-0 max-w-full grid-cols-[minmax(5rem,7rem)_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-md bg-muted/60 px-3 py-2">
          {preview.rows.map((row) => (
            <React.Fragment key={`${row.label}:${row.value}`}>
              <dt className="min-w-0 font-medium text-muted-foreground [overflow-wrap:anywhere]" title={row.label}>
                {row.label}
              </dt>
              <dd className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]" title={formatIsoTimestampsInText(row.value)}>
                {formatIsoTimestampsInText(row.value)}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}
      {preview.body !== undefined ? (
        <div
          className="min-w-0 max-w-full whitespace-pre-wrap rounded-md border border-border bg-canvas px-3 py-2 [overflow-wrap:anywhere]"
          title={preview.body}
        >
          {preview.body}
        </div>
      ) : null}
    </div>
  )
}

/** #191: a contractor packet's body is the plain-text scope; render its sections and bullet lists. */
const PACKET_KINDS = new Set(['book_contractor', 'request_quotes'])
const FIELD_EDIT_KINDS = new Set(['update_case', 'update_ticket'])

function shownValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)'
  if (typeof value === 'string') return formatIsoTimestampsInText(value)
  return JSON.stringify(value)
}

function fieldDiffs(card: RileyCard): RileyCardPreviewDiff[] {
  if (!FIELD_EDIT_KINDS.has(card.kind) || !Array.isArray(card.payload.field_diff)) return card.preview.diffs ?? []
  return card.payload.field_diff.flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return []
    const row = value as Record<string, unknown>
    if (typeof row.field !== 'string') return []
    const label = typeof row.label === 'string' && row.label ? row.label : row.field.replaceAll('_', ' ')
    const before = 'before_display' in row ? row.before_display : row.before
    const after = 'after_display' in row ? row.after_display : row.after
    return [{ label, before: shownValue(before), after: shownValue(after) }]
  })
}

function CardPreview({ card }: { card: RileyCard }) {
  const { rows = [], body, note } = card.preview
  const diffs = fieldDiffs(card)
  return (
    <div className="mt-2 min-w-0 max-w-full space-y-2 overflow-hidden text-sm text-foreground/85" data-slot="card-preview">
      {rows.length ? (
        <dl className="grid min-w-0 max-w-full grid-cols-[minmax(5rem,7rem)_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-md bg-muted/60 px-3 py-2">
          {rows.map((row) => (
            <React.Fragment key={`${row.label}:${row.value}`}>
              <dt className="min-w-0 font-medium text-muted-foreground [overflow-wrap:anywhere]" title={row.label}>
                {row.label}
              </dt>
              <dd className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]" title={formatIsoTimestampsInText(row.value)}>
                {formatIsoTimestampsInText(row.value)}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}
      {body && PACKET_KINDS.has(card.kind) ? (
        <div className="min-w-0 max-w-full rounded-md border border-border bg-canvas px-3 py-2 [overflow-wrap:anywhere]" title={body}>
          <PacketText text={body} />
        </div>
      ) : body ? (
        <div
          className="min-w-0 max-w-full whitespace-pre-wrap rounded-md border border-border bg-canvas px-3 py-2 [overflow-wrap:anywhere]"
          title={body}
        >
          {body}
        </div>
      ) : null}
      {note ? (
        <p className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]" title={note}>
          {note}
        </p>
      ) : null}
      {diffs.length ? (
        <section className="space-y-1.5 rounded-md border border-border px-3 py-2" aria-label="Proposed changes">
          {diffs.map((diff) => (
            <div key={`${diff.label}:${diff.before}:${diff.after}`}>
              <p className="flex min-w-0 items-center gap-1 font-medium [overflow-wrap:anywhere]" title={diff.label}>
                <FileDiff className="size-3.5" /> {diff.label}
              </p>
              <p
                className="min-w-0 whitespace-pre-wrap text-destructive line-through [overflow-wrap:anywhere]"
                title={formatIsoTimestampsInText(diff.before)}
              >
                {formatIsoTimestampsInText(diff.before) || '(empty)'}
              </p>
              <p
                className="min-w-0 whitespace-pre-wrap text-success [overflow-wrap:anywhere]"
                title={formatIsoTimestampsInText(diff.after)}
              >
                {formatIsoTimestampsInText(diff.after) || '(empty)'}
              </p>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  )
}

type BulkSummary = { label?: string; before?: string; after?: string }

type BulkRow = {
  row_id: string
  case?: { label?: string; href?: string; case_number?: string }
  before?: Record<string, unknown>
  change?: Record<string, unknown>
  summary?: BulkSummary
  preflight?: { ready?: boolean; blockers?: string[] }
}

function bulkRows(card: RileyCard): BulkRow[] {
  if (card.kind !== 'bulk_action' || !Array.isArray(card.payload.rows)) return []
  return card.payload.rows.flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return []
    const row = value as Partial<BulkRow>
    return typeof row.row_id === 'string' ? [row as BulkRow] : []
  })
}

function compactValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)'
  if (typeof value === 'string') return formatIsoTimestampsInText(value)
  if (Array.isArray(value)) return value.map(compactValue).join(', ')
  if (typeof value === 'object')
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${key.replaceAll('_', ' ')}: ${compactValue(item)}`)
      .join(' · ')
  return String(value)
}

function bulkChangeText(row: BulkRow): { prefix: string; before: string; after: string } {
  const summary = row.summary
  if (summary && (typeof summary.before === 'string' || typeof summary.after === 'string')) {
    return {
      prefix: summary.label ? `${summary.label}: ` : '',
      before: summary.before || '(none)',
      after: summary.after || '(none)',
    }
  }
  return { prefix: '', before: compactValue(row.before), after: compactValue(row.change) }
}

function BulkPreview({
  card,
  selected,
  onToggle,
  disabled,
}: {
  card: RileyCard
  selected: ReadonlySet<string>
  onToggle: (rowId: string) => void
  disabled: boolean
}) {
  const rows = bulkRows(card)
  return (
    <div className="mt-3 space-y-2" data-slot="bulk-rows">
      <p className="text-xs text-muted-foreground">
        {selected.size} of {rows.length} selected · untick any row before the single approval
      </p>
      <ul className="max-h-96 space-y-2 overflow-y-auto" aria-label="Cases in this bulk action">
        {rows.map((row) => {
          const blockers = row.preflight?.blockers ?? []
          const change = bulkChangeText(row)
          return (
            <li key={row.row_id} className="rounded-md border border-border bg-canvas px-3 py-2 text-sm">
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-primary"
                  checked={selected.has(row.row_id)}
                  disabled={disabled}
                  onChange={() => onToggle(row.row_id)}
                  aria-label={`Include ${row.case?.case_number ?? row.case?.label ?? 'case'}`}
                />
                <span className="min-w-0 flex-1">
                  {row.case?.href ? (
                    <a className="font-medium text-primary hover:underline" href={row.case.href}>
                      {row.case.label ?? row.case.case_number ?? 'Case'}
                    </a>
                  ) : (
                    <span className="font-medium">{row.case?.label ?? 'Case'}</span>
                  )}
                  <span className="mt-1 block text-xs text-muted-foreground [overflow-wrap:anywhere]">
                    {change.prefix}
                    <span className="line-through">{change.before}</span>
                    {' → '}
                    <span className="text-foreground">{change.after}</span>
                  </span>
                  {blockers.length ? (
                    <span className="mt-1 block text-xs text-warning">Blocked unless resolved: {blockers.join(' · ')}</span>
                  ) : null}
                </span>
              </label>
            </li>
          )
        })}
      </ul>
      <p className="text-xs text-muted-foreground">
        Reversible rows can be undone for 24 hours. Delivered customer emails cannot be recalled.
      </p>
    </div>
  )
}

function BulkResult({ card }: { card: RileyCard }) {
  const result = card.result as { rows?: unknown[]; undo?: { summary?: unknown } } | null | undefined
  if (!Array.isArray(result?.rows)) return null
  return (
    <div className="mt-2 space-y-1.5" data-slot="bulk-results">
      <p className="text-xs font-medium text-muted-foreground">Per-case results</p>
      <ul className="space-y-1 text-xs">
        {result.rows.flatMap((value, index) => {
          if (!value || typeof value !== 'object' || Array.isArray(value)) return []
          const row = value as Record<string, unknown>
          const ref = row.case && typeof row.case === 'object' ? (row.case as Record<string, unknown>) : {}
          const status = String(row.status ?? 'unknown')
          return [
            <li key={String(row.row_id ?? index)} className="rounded-md border border-border px-2.5 py-1.5">
              <span className="font-medium">{String(ref.label ?? ref.case_number ?? 'Case')}</span>
              <span
                className={status === 'failed' ? 'ml-2 text-destructive' : status === 'partial' ? 'ml-2 text-warning' : 'ml-2 text-success'}
              >
                {status}
              </span>
              {row.detail ? (
                <span className="mt-0.5 block text-muted-foreground [overflow-wrap:anywhere]">{compactValue(row.detail)}</span>
              ) : null}
            </li>,
          ]
        })}
      </ul>
      {typeof result.undo?.summary === 'string' ? <p className="text-xs text-muted-foreground">Undo: {result.undo.summary}</p> : null}
    </div>
  )
}

/** "Expires in 3 h" for a proposed card with an expiry; "Expired" once past it (the server refuses the confirm). */
export function expiryLabel(expiresAt: string | null | undefined, now: number = Date.now()): string | null {
  if (!expiresAt) return null
  const at = Date.parse(expiresAt)
  if (Number.isNaN(at)) return null
  const ms = at - now
  if (ms <= 0) return 'Expired; ask Riley again'
  const minutes = Math.ceil(ms / 60_000)
  if (minutes < 60) return `Expires in ${minutes} min`
  return `Expires in ${Math.floor(minutes / 60)} h`
}

const RILEY_SYNC: Record<string, string> = {
  ok: 'Riley was told',
  no_session: 'Riley had no session to tell',
  not_bound: 'Riley was not told',
}

function syncSentence(sync: unknown): string | null {
  if (typeof sync !== 'string' || !sync) return null
  if (RILEY_SYNC[sync]) return RILEY_SYNC[sync]
  if (sync.startsWith('failed')) return 'Riley could not be told'
  return null
}

function textOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/**
 * A card's outcome as one sentence (patchbay#198): never the raw `result` object. Known fields become words; the
 * rest stays in the audit log.
 */
export function cardResultSentence(card: Pick<RileyCard, 'state' | 'result'>, lapsed = false): string {
  const result = (card.result ?? {}) as Record<string, unknown>
  const sync = syncSentence(result.riley_sync)
  const parts: string[] = []
  const state = lapsed ? 'expired' : card.state
  if (state === 'dismissed') {
    parts.push('Dismissed; nothing ran')
    const reason = textOf(result.reason)
    if (reason) parts.push(`reason: ${reason}`)
  } else if (state === 'expired') {
    parts.push('Expired; ask Riley again')
  } else if (state === 'failed' || result.ok === false) {
    const why = textOf(result.detail) ?? textOf(result.error) ?? textOf(result.message)
    parts.push(why ? `Failed: ${why}` : 'Failed')
  } else if (state === 'confirmed' || state === 'edited_confirmed') {
    parts.push(
      textOf(result.outcome) ??
        textOf(result.summary) ??
        textOf(result.message) ??
        (state === 'edited_confirmed' ? 'Edited, then completed' : 'Completed'),
    )
    if (textOf(result.desk_thread_id)) parts.push('posted to Desk')
    if (textOf(result.time_entry_id)) parts.push('time logged in Desk')
  } else {
    return ''
  }
  if (sync) parts.push(sync)
  return parts.join('; ')
}

function decidedAtLabel(at: string | null | undefined): string | null {
  if (!at) return null
  const t = Date.parse(at)
  if (Number.isNaN(t)) return null
  return formatWhen(at, { style: 'absolute' })
}

/** Payload keys whose edited value is the text that leaves (shown in the edited preview, not as JSON). */
const BODY_KEYS = ['body_text', 'body', 'body_html', 'message', 'scope', 'note']

function editedPreviewOf(raw: string): { ok: true; body?: string; rows: { label: string; value: string }[] } | { ok: false } {
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { ok: false }
    const entries = Object.entries(parsed as Record<string, unknown>)
    const bodyKey = BODY_KEYS.find((k) => typeof (parsed as Record<string, unknown>)[k] === 'string')
    const body = bodyKey ? String((parsed as Record<string, unknown>)[bodyKey]) : undefined
    const rows = entries
      .filter(([k, v]) => k !== bodyKey && v !== null && v !== '' && typeof v !== 'object')
      .map(([k, v]) => ({ label: k.replaceAll('_', ' '), value: String(v) }))
    return { ok: true, body, rows }
  } catch {
    return { ok: false }
  }
}

/** A send card the guard allowed with a warning (warn-then-allow, round 7): the person reads it, then confirms. */
export interface CardAcknowledgement {
  codes: string[]
  messages: string[]
}

export function acknowledgementOf(error: unknown): CardAcknowledgement | null {
  const e = error as { code?: unknown; body?: { warnings?: { code?: string; message?: string }[]; acknowledge_codes?: string[] } }
  if (e?.code !== 'warnings_unacknowledged') return null
  const codes = e.body?.acknowledge_codes ?? (e.body?.warnings ?? []).map((w) => String(w.code))
  const messages = (e.body?.warnings ?? [])
    .filter((w) => !codes.length || codes.includes(String(w.code)))
    .map((w) => String(w.message ?? w.code))
  return { codes, messages: messages.length ? messages : codes }
}

/**
 * Poll a confirmed async card's real state (#417). `Done` at confirm time was a lie for a re-triage: the note and the
 * new suggestion land minutes later. Polls every few seconds while the server says running, then stops.
 */
export function useCardProgress(
  card: Pick<RileyCard, 'id' | 'kind' | 'state'>,
  load?: (cardId: string) => Promise<RileyCardProgress | null>,
  everyMs = 4000,
): RileyCardProgress | null {
  const [progress, setProgress] = React.useState<RileyCardProgress | null>(null)
  const confirmed = card.state === 'confirmed' || card.state === 'edited_confirmed'
  const watch = Boolean(load) && confirmed && ASYNC_CARD_KINDS.has(card.kind)
  const loadRef = React.useRef(load)
  loadRef.current = load
  React.useEffect(() => {
    if (!watch) {
      setProgress(null)
      return
    }
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const tick = async () => {
      let next: RileyCardProgress | null = null
      try {
        next = (await loadRef.current?.(card.id)) ?? null
      } catch {
        next = null // an unreadable state is not "done": keep the last known one and try again
        if (live) timer = setTimeout(() => void tick(), everyMs)
        return
      }
      if (!live) return
      setProgress(next)
      if (next?.state === 'running') timer = setTimeout(() => void tick(), everyMs)
    }
    void tick()
    return () => {
      live = false
      if (timer) clearTimeout(timer)
    }
  }, [watch, card.id, everyMs])
  // Until the first read lands, an async card is still being checked, never "Done".
  return watch ? (progress ?? { card_id: card.id, state: 'running', headline: 'Running: checking the result…', items: [] }) : null
}

function CardProgressLine({
  progress,
  onJump,
  className,
}: {
  progress: RileyCardProgress
  onJump?: (target: string) => void
  className?: string
}) {
  return (
    <div className={cn('min-w-0 text-xs text-muted-foreground', className)} data-slot="card-progress" data-progress={progress.state}>
      <p
        role={progress.state === 'failed' ? 'alert' : 'status'}
        className={cn('flex min-w-0 items-center gap-1.5 [overflow-wrap:anywhere]', progress.state === 'failed' && 'text-destructive')}
      >
        {progress.state === 'running' ? <Loader2 className="size-3 shrink-0 animate-spin" /> : null}
        {progress.headline}
      </p>
      {progress.state === 'done' && onJump ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {progress.items
            .filter((item) => item.target)
            .map((item) => (
              <Button key={item.label} size="sm" variant="ghost" onClick={() => onJump(item.target as string)}>
                {item.kind === 'suggestion' ? 'Open the ' : 'Jump to the '}
                {item.label}
              </Button>
            ))}
        </div>
      ) : null}
    </div>
  )
}

export function RileyConfirmCard({
  card,
  onConfirm,
  onDismiss,
  onUndismiss,
  onUseInReplyBox,
  onBulkUndo,
  onTriageUndo,
  onStaffUndo,
  onHide,
  onFeedback,
  onTidyServiceHistory,
  getProgress,
  onJump,
  collapsible,
  open,
  onOpenChange,
  className,
  debugRaw = false,
}: {
  card: RileyCard
  onConfirm?: (card: RileyCard, editedPayload?: Record<string, unknown>, acknowledged?: string[]) => Promise<RileyCard>
  onDismiss?: (card: RileyCard, reason?: string | null) => Promise<RileyCard>
  /** Undo dismiss (patchbay#198): offered while the API says `can_undismiss`. */
  onUndismiss?: (card: RileyCard) => Promise<RileyCard>
  /** #746: a reply card (send_reply, draft_reply) opens in the reply box; it never sends from the card. */
  onUseInReplyBox?: (card: RileyCard) => Promise<RileyCard>
  /** Restore successful reversible rows from this bulk card, once, within 24 hours. */
  onBulkUndo?: (card: RileyCard) => Promise<RileyCard>
  /** Restore fields still unchanged since this Riley triage packet, once, within 24 hours. */
  onTriageUndo?: (card: RileyCard) => Promise<RileyCard>
  /** Undo a staff-instructed Riley write, once, within its 24-hour window (#1097). */
  onStaffUndo?: (card: RileyCard) => Promise<RileyCard>
  /** Hide this decided card from the conversation for this person. */
  onHide?: (card: RileyCard) => void
  onFeedback?: (feedback: RileyFeedbackTarget) => Promise<void> | void
  /** Draft-only #838 rewrite. The returned text still needs this card's explicit Confirm edit. */
  onTidyServiceHistory?: (card: RileyCard, text: string) => Promise<{ text: string; stripped?: string[] }>
  /** Real state of a confirmed async card (a re-triage): Running…, then Done with links, or Failed. */
  getProgress?: (cardId: string) => Promise<RileyCardProgress | null>
  /** Jump to a place on the case (`turn:<id>`) named by a finished card's result. */
  onJump?: (target: string) => void
  /** A pending card in a multi-card reply is one summary row until opened (#417). */
  collapsible?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
  /** Superadmin-only caller gate for the legacy payload inspector. Never enable from card data. */
  debugRaw?: boolean
}) {
  const [visibleCard, setVisibleCard] = React.useState(card)
  const [localOpen, setLocalOpen] = React.useState(false)
  const proposalOpen = open ?? localOpen
  const setProposalOpen = (next: boolean) => {
    setLocalOpen(next)
    onOpenChange?.(next)
  }
  const progress = useCardProgress(visibleCard, getProgress)
  const [action, setAction] = React.useState<
    'confirm' | 'dismiss' | 'undismiss' | 'bulk_undo' | 'triage_undo' | 'staff_undo' | 'reply_box' | null
  >(null)
  // A decided card is one line by default (patchbay#198); the person can open it again.
  const [expanded, setExpanded] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [editedPayload, setEditedPayload] = React.useState(() => JSON.stringify(card.payload, null, 2))
  const [error, setError] = React.useState<string | null>(null)
  const [ack, setAck] = React.useState<(CardAcknowledgement & { payload?: Record<string, unknown> }) | null>(null)
  const [bulkSelected, setBulkSelected] = React.useState<ReadonlySet<string>>(
    () =>
      new Set(
        Array.isArray(card.payload.selected_row_ids) ? card.payload.selected_row_ids.map(String) : bulkRows(card).map((row) => row.row_id),
      ),
  )
  const actionInFlight = React.useRef(false)
  const lastConfirm = React.useRef<{ payload?: Record<string, unknown>; acknowledged?: string[] }>({})

  React.useEffect(() => {
    setVisibleCard(card)
    setEditedPayload(JSON.stringify(card.payload, null, 2))
    setBulkSelected(
      new Set(
        Array.isArray(card.payload.selected_row_ids) ? card.payload.selected_row_ids.map(String) : bulkRows(card).map((row) => row.row_id),
      ),
    )
  }, [card])

  const expiry = visibleCard.state === 'proposed' ? expiryLabel(visibleCard.expires_at) : null
  const lapsed = expiry?.startsWith('Expired') ?? false
  const proposed = visibleCard.state === 'proposed' && !lapsed
  // Edit and Dismiss are decisions too: a role that cannot confirm this kind sees the card read-only (#67).
  const canConfirm = visibleCard.can_confirm
  const canDismiss = visibleCard.can_dismiss ?? canConfirm
  const blockingErrors = visibleCard.blocking_errors ?? []
  // A do_it_yourself card (#274) is decided with Mark done (minutes + outcome), never a raw JSON edit.
  const diy = visibleCard.kind === DIY_KIND
  const bulk = visibleCard.kind === 'bulk_action'
  const closeout = visibleCard.kind === 'conduit_closeout'
  const followUp = visibleCard.kind === 'notify_teammate' && typeof visibleCard.payload.follow_up === 'object'
  const report = visibleCard.kind === REPORT_KIND
  // #838: `editable` never means "show JSON". Staff get only an explicitly typed editor; a caller may opt a
  // verified superadmin into the raw payload inspector for debugging.
  const canEdit =
    canConfirm &&
    !diy &&
    !bulk &&
    ((closeout && visibleCard.editable !== false) || followUp || (debugRaw && visibleCard.executor === 'patchbay'))
  const rawEditing = editing && !closeout && !followUp
  const confirmPayload = bulk ? { selected_row_ids: [...bulkSelected] } : undefined
  const decide = async (
    kind: 'confirm' | 'dismiss',
    payload?: Record<string, unknown>,
    acknowledged?: string[],
    reason?: string | null,
  ) => {
    if (!proposed || actionInFlight.current) return false
    if (kind === 'confirm' && (!canConfirm || !onConfirm)) return false
    if (kind === 'dismiss' && (!canDismiss || !onDismiss)) return false
    actionInFlight.current = true
    setAction(kind)
    setError(null)
    if (kind === 'confirm') lastConfirm.current = { payload, acknowledged }
    try {
      const pending = kind === 'confirm' ? onConfirm?.(visibleCard, payload, acknowledged) : onDismiss?.(visibleCard, reason ?? null)
      if (!pending) return false
      const next = kind === 'confirm' ? await withDeadline(pending, CONFIRM_WAIT_MS) : await pending
      if (next) setVisibleCard(next)
      setEditing(false)
      setAck(null)
      return true
    } catch (caught) {
      const needed = kind === 'confirm' ? acknowledgementOf(caught) : null
      if (needed) setAck({ ...needed, payload })
      else {
        const fallback = caught instanceof Error ? caught.message : 'The card could not be updated.'
        setError(kind === 'confirm' ? confirmFailure(caught) : fallback)
      }
      return false
    } finally {
      actionInFlight.current = false
      setAction(null)
    }
  }

  const confirmEdit = () => {
    try {
      const parsed = JSON.parse(editedPayload) as unknown
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('The edited payload must be a JSON object.')
      void decide('confirm', parsed as Record<string, unknown>)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The edited payload is not valid JSON.')
    }
  }

  const moveToReplyBox = async () => {
    if (!onUseInReplyBox || actionInFlight.current) return
    actionInFlight.current = true
    setAction('reply_box')
    setError(null)
    try {
      setVisibleCard(await onUseInReplyBox(visibleCard))
      setExpanded(false)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The reply could not be moved to the reply box.')
    } finally {
      actionInFlight.current = false
      setAction(null)
    }
  }

  const undismiss = async () => {
    if (!onUndismiss || actionInFlight.current) return
    actionInFlight.current = true
    setAction('undismiss')
    setError(null)
    try {
      setVisibleCard(await onUndismiss(visibleCard))
      setExpanded(false)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The card could not be reopened.')
    } finally {
      actionInFlight.current = false
      setAction(null)
    }
  }

  const undoBulk = async () => {
    if (!onBulkUndo || actionInFlight.current) return
    actionInFlight.current = true
    setAction('bulk_undo')
    setError(null)
    try {
      setVisibleCard(await onBulkUndo(visibleCard))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The bulk action could not be undone.')
    } finally {
      actionInFlight.current = false
      setAction(null)
    }
  }

  const undoTriage = async () => {
    if (!onTriageUndo || actionInFlight.current) return
    actionInFlight.current = true
    setAction('triage_undo')
    setError(null)
    try {
      setVisibleCard(await onTriageUndo(visibleCard))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The triage packet could not be undone.')
    } finally {
      actionInFlight.current = false
      setAction(null)
    }
  }

  const undoStaff = async () => {
    if (!onStaffUndo || actionInFlight.current) return
    actionInFlight.current = true
    setAction('staff_undo')
    setError(null)
    try {
      setVisibleCard(await onStaffUndo(visibleCard))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The action could not be undone.')
    } finally {
      actionInFlight.current = false
      setAction(null)
    }
  }

  const statusTone =
    progress?.state === 'failed' || visibleCard.state === 'failed'
      ? 'danger'
      : visibleCard.state === 'proposed' && !lapsed
        ? 'outline'
        : visibleCard.state === 'dismissed' || lapsed
          ? 'neutral'
          : 'success'
  const scrub = scrubSummary(visibleCard.scrub_report)
  const title = visibleCard.title ?? visibleCard.kind.replaceAll('_', ' ')
  const decided = !proposed
  // An async card's confirm only started the work: say so until the case's own events show the result (#417).
  const sentence = decided && !progress ? cardResultSentence(visibleCard, lapsed) : ''
  const stateLabel = progress
    ? progress.state === 'running'
      ? 'Running…'
      : progress.state === 'failed'
        ? 'Failed'
        : 'Done'
    : action
      ? action === 'confirm'
        ? 'Confirming…'
        : action === 'dismiss'
          ? 'Dismissing…'
          : action === 'bulk_undo' || action === 'triage_undo' || action === 'staff_undo'
            ? 'Undoing…'
            : 'Reopening…'
      : lapsed
        ? STATE_LABEL.expired
        : STATE_LABEL[visibleCard.state]
  const canUndo = visibleCard.state === 'dismissed' && visibleCard.can_undismiss === true && Boolean(onUndismiss) && !lapsed
  const bulkUndoUntil = textOf((visibleCard.result as Record<string, unknown> | null | undefined)?.undo_until)
  const canBulkUndo =
    bulk &&
    Boolean(onBulkUndo) &&
    Boolean((visibleCard.result as Record<string, unknown> | null | undefined)?.can_undo) &&
    Boolean(bulkUndoUntil && Date.parse(bulkUndoUntil) > Date.now())
  const triage = visibleCard.kind === 'hub.write' && visibleCard.payload.tool === 'hub.apply_desk_triage_fields'
  const triageUndoUntil = textOf((visibleCard.result as Record<string, unknown> | null | undefined)?.undo_until)
  const canTriageUndo =
    triage &&
    Boolean(onTriageUndo) &&
    Boolean((visibleCard.result as Record<string, unknown> | null | undefined)?.can_undo) &&
    Boolean(triageUndoUntil && Date.parse(triageUndoUntil) > Date.now())
  const staffResult = visibleCard.result as Record<string, unknown> | null | undefined
  const staffUndoUntil = textOf(staffResult?.undo_until)
  const canStaffUndo =
    Boolean(onStaffUndo) &&
    staffResult?.staff_request === true &&
    Boolean(staffResult?.action_id) &&
    Boolean(staffResult?.can_undo) &&
    Boolean(staffUndoUntil && Date.parse(staffUndoUntil) > Date.now())
  const decidedActions = decided ? (
    <>
      {canUndo ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={Boolean(action)}
          onClick={() => void undismiss()}
          title="Put this card back to decide again"
        >
          <Undo2 /> Undo dismiss
        </Button>
      ) : null}
      {canBulkUndo ? (
        <Button size="sm" variant="ghost" disabled={Boolean(action)} onClick={() => void undoBulk()} title="Restore prior values">
          <Undo2 /> Undo bulk changes
        </Button>
      ) : null}
      {canTriageUndo ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={Boolean(action)}
          onClick={() => void undoTriage()}
          title="Restore only values Riley changed that nobody changed again"
        >
          <Undo2 /> Undo triage
        </Button>
      ) : null}
      {canStaffUndo ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={Boolean(action)}
          onClick={() => void undoStaff()}
          title="Undo this Riley write from the last 24 hours"
        >
          <Undo2 /> Undo
        </Button>
      ) : null}
      {onHide ? (
        <Button size="sm" variant="ghost" onClick={() => onHide(visibleCard)} title="Hide this card from the conversation (only for you)">
          <EyeOff /> Hide
        </Button>
      ) : null}
    </>
  ) : null

  if (decided && !expanded) {
    const at = decidedAtLabel(visibleCard.decided_at)
    return (
      <section
        aria-label={`Confirm card: ${visibleCard.kind}`}
        data-card-id={visibleCard.id}
        data-card-kind={visibleCard.kind}
        data-status={lapsed ? 'expired' : visibleCard.state}
        data-collapsed="true"
        className={cn('min-w-0 max-w-full overflow-hidden rounded-lg border border-border bg-card/70 px-2.5 py-1.5 text-sm', className)}
      >
        <div className="min-w-0" data-slot="decided-card-row">
          <div className="flex min-w-0 items-center gap-1.5" data-slot="decided-card-head">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              aria-expanded={false}
              aria-label={`Show card: ${title}`}
              className="inline-flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className="size-4 shrink-0" />
              <span className="truncate font-medium text-foreground/85" title={title}>
                {title}
              </span>
            </button>
            <Chip tone={statusTone} size="sm" className="shrink-0" data-slot="decided-card-state">
              {progress?.state === 'running' ? (
                <Loader2 className="animate-spin" />
              ) : (visibleCard.state === 'confirmed' || visibleCard.state === 'edited_confirmed') && progress?.state !== 'failed' ? (
                <Check />
              ) : null}
              {stateLabel}
            </Chip>
          </div>
          {at || canUndo || canBulkUndo || canTriageUndo || canStaffUndo || onHide ? (
            <div className="mt-1 flex min-w-0 flex-wrap items-center justify-end gap-1" data-slot="decided-card-actions">
              {at ? <span className="mr-auto shrink-0 pl-5.5 text-2xs text-subtle">{at}</span> : null}
              {decidedActions}
            </div>
          ) : null}
        </div>
        {progress ? <CardProgressLine progress={progress} onJump={onJump} className="ml-5.5" /> : null}
        {sentence ? (
          <p
            className="ml-5.5 min-w-0 whitespace-pre-wrap text-xs text-muted-foreground [overflow-wrap:anywhere]"
            data-slot="card-result"
            title={sentence}
          >
            {sentence}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="ml-5.5 text-xs text-destructive">
            {error}
          </p>
        ) : null}
      </section>
    )
  }

  if (collapsible && proposed && !proposalOpen) {
    return (
      <section
        aria-label={`Confirm card: ${visibleCard.kind}`}
        data-card-id={visibleCard.id}
        data-card-kind={visibleCard.kind}
        data-status={visibleCard.state}
        data-collapsed="true"
        className={cn('min-w-0 max-w-full overflow-hidden rounded-lg border border-primary/30 bg-card px-2.5 py-1.5 text-sm', className)}
      >
        <button
          type="button"
          onClick={() => setProposalOpen(true)}
          aria-expanded={false}
          aria-label={`Show card: ${title}`}
          className="flex w-full min-w-0 cursor-pointer items-center gap-1.5 text-left"
        >
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">
            <span className="font-medium">{title}</span>
            {visibleCard.impact_text ? <span className="text-muted-foreground"> · {visibleCard.impact_text}</span> : null}
          </span>
          <Chip tone="outline" size="sm" className="shrink-0">
            {stateLabel}
          </Chip>
        </button>
      </section>
    )
  }

  return (
    <section
      // biome-ignore lint/a11y/noNoninteractiveTabindex: a/e/x operate on the focused card.
      tabIndex={0}
      aria-label={`Confirm card: ${visibleCard.kind}`}
      data-card-id={visibleCard.id}
      data-card-kind={visibleCard.kind}
      data-status={visibleCard.state}
      onKeyDown={(event) => {
        if (!proposed || event.target !== event.currentTarget || event.metaKey || event.ctrlKey || event.altKey) return
        if (event.key === 'a' && canConfirm && (!bulk || bulkSelected.size > 0)) void decide('confirm', confirmPayload)
        else if (event.key === 'e' && canEdit) setEditing(true)
        else if (event.key === 'x' && canDismiss) void decide('dismiss', undefined, undefined, null)
        else return
        event.preventDefault()
      }}
      className={cn(
        'min-w-0 max-w-full overflow-hidden rounded-lg border bg-card p-3.5 shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
        proposed ? 'border-primary/40' : 'border-border',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-1.5">
            <Chip tone="outline" className="max-w-full truncate font-mono text-2xs" title={visibleCard.kind}>
              {visibleCard.kind}
            </Chip>
            <CardPathChip card={visibleCard} />
            <span className="text-2xs text-subtle">runs in {visibleCard.executor === 'riley' ? 'Riley' : 'Patchbay'}</span>
          </div>
          <h3 className="min-w-0 text-base font-semibold [overflow-wrap:anywhere]" title={title}>
            {title}
          </h3>
        </div>
        <Chip tone={statusTone} className="shrink-0">
          {action || progress?.state === 'running' ? (
            <Loader2 className="animate-spin" />
          ) : (visibleCard.state === 'confirmed' || visibleCard.state === 'edited_confirmed') && progress?.state !== 'failed' ? (
            <Check />
          ) : null}
          {stateLabel}
        </Chip>
        {collapsible && proposed ? (
          <button
            type="button"
            onClick={() => setProposalOpen(false)}
            aria-expanded
            aria-label={`Collapse card: ${title}`}
            title="Collapse to one line"
            className="inline-flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          >
            <ChevronDown className="size-4" />
          </button>
        ) : null}
        {decided ? (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            aria-expanded
            aria-label={`Collapse card: ${title}`}
            title="Collapse to one line"
            className="inline-flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          >
            <ChevronDown className="size-4" />
          </button>
        ) : null}
      </div>

      {rawEditing ? (
        <EditedPreview raw={editedPayload} />
      ) : editing && (closeout || followUp) ? null : bulk ? (
        <BulkPreview
          card={visibleCard}
          selected={bulkSelected}
          disabled={!proposed || !canConfirm || Boolean(action)}
          onToggle={(rowId) =>
            setBulkSelected((current) => {
              const next = new Set(current)
              if (next.has(rowId)) next.delete(rowId)
              else next.add(rowId)
              return next
            })
          }
        />
      ) : report ? (
        <ReportCardBody payload={visibleCard.payload} />
      ) : diy ? (
        <DiyBody payload={visibleCard.payload} caseId={visibleCard.case_id} cardId={visibleCard.id} />
      ) : (
        <CardPreview card={visibleCard} />
      )}

      <p className="mt-2 flex min-w-0 max-w-full items-start gap-1.5 text-sm" data-slot="impact">
        <Eye className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 [overflow-wrap:anywhere]" title={visibleCard.impact_text || undefined} data-testid="impact-value">
          <span className="font-medium">{IMPACT_LABEL[visibleCard.audience]}</span>
          {visibleCard.impact_text ? <>: {visibleCard.impact_text}</> : null}
        </span>
      </p>
      {scrub ? (
        <p
          className="mt-1 flex min-w-0 items-start gap-1.5 text-xs text-muted-foreground [overflow-wrap:anywhere]"
          data-slot="scrub-report"
        >
          <ShieldCheck className="size-3.5 text-success" /> {scrub}
        </p>
      ) : null}

      {expiry ? (
        <p className={cn('mt-1 text-xs', lapsed ? 'text-destructive' : 'text-muted-foreground')} data-slot="expiry">
          {expiry}
        </p>
      ) : null}

      {blockingErrors.length ? (
        <div
          role="alert"
          className="mt-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm"
          data-slot="blocking-errors"
        >
          {blockingErrors.map((item) => (
            <p key={`${item.code}:${item.field ?? ''}:${item.message}`} className="text-destructive">
              {item.message}
            </p>
          ))}
          <p className="mt-1 font-medium text-foreground">Ask Riley to fix</p>
        </div>
      ) : null}

      {editing && closeout ? (
        <CloseoutEditForm
          card={visibleCard}
          busy={Boolean(action)}
          onConfirm={(edit) => void decide('confirm', edit)}
          onCancel={() => setEditing(false)}
          onTidy={onTidyServiceHistory}
        />
      ) : null}

      {editing && followUp ? (
        <FollowUpEditForm
          card={visibleCard}
          busy={Boolean(action)}
          onConfirm={(edit) => void decide('confirm', edit)}
          onCancel={() => setEditing(false)}
        />
      ) : null}

      {rawEditing ? (
        <div className="mt-3 space-y-2">
          <label className="block text-xs font-medium" htmlFor={`card-payload-${visibleCard.id}`}>
            Debug frozen payload (superadmin)
          </label>
          <textarea
            id={`card-payload-${visibleCard.id}`}
            value={editedPayload}
            onChange={(event) => setEditedPayload(event.target.value)}
            className="min-h-36 w-full min-w-0 max-w-full rounded-md border border-border-strong bg-canvas p-2 font-mono text-xs outline-none focus:border-primary"
          />
          <div className="flex gap-2">
            <Button variant="primary" size="sm" disabled={Boolean(action)} onClick={confirmEdit}>
              Confirm edit
            </Button>
            <Button size="sm" disabled={Boolean(action)} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {ack ? (
        <div role="alert" className="mt-2 space-y-2 rounded-md border border-warning/40 bg-warning-soft px-3 py-2 text-sm" data-slot="ack">
          <p className="font-medium text-warning">The guard allowed this with a warning</p>
          <ul className="list-disc pl-4">
            {ack.messages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
          <div className="flex gap-2">
            <Button size="sm" variant="primary" disabled={Boolean(action)} onClick={() => void decide('confirm', ack.payload, ack.codes)}>
              I have read this · confirm anyway
            </Button>
            <Button size="sm" disabled={Boolean(action)} onClick={() => setAck(null)}>
              Not now
            </Button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="mt-2 space-y-2">
          <p className="text-sm text-destructive">{error}</p>
          {proposed && canConfirm && onConfirm ? (
            <Button
              size="sm"
              variant="primary"
              disabled={Boolean(action)}
              onClick={() => void decide('confirm', lastConfirm.current.payload ?? confirmPayload, lastConfirm.current.acknowledged)}
            >
              Retry
            </Button>
          ) : null}
        </div>
      ) : null}

      {progress ? <CardProgressLine progress={progress} onJump={onJump} className="mt-2 rounded-md bg-muted px-3 py-2 text-sm" /> : null}
      {sentence ? (
        <p data-slot="card-result" className="mt-2 rounded-md bg-muted px-3 py-2 text-sm">
          {sentence}
        </p>
      ) : null}

      {decided && bulk ? <BulkResult card={visibleCard} /> : null}

      {diy && proposed && canConfirm ? (
        <DiyDoneForm
          caseId={visibleCard.case_id}
          trivial={diyPayload(visibleCard.payload).trivial}
          busy={Boolean(action)}
          onDone={(edit) => decide('confirm', edit)}
        />
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {diy && canConfirm ? null : proposed && blockingErrors.length && !editing ? (
          <Button variant="primary" disabled title="Riley must fix the invalid value before this can run">
            Confirm
          </Button>
        ) : proposed && canConfirm && !editing && isReplyCard(visibleCard) && onUseInReplyBox ? (
          // #746: one way to send a Riley reply. It opens in the reply box (guard, To/Cc, post-send chips), never here.
          <Button
            variant="primary"
            disabled={Boolean(action)}
            onClick={() => void moveToReplyBox()}
            title="Open this reply in the reply box"
          >
            Use in reply box
          </Button>
        ) : report && proposed && canConfirm && !editing ? (
          <ReportCardActions
            card={visibleCard}
            busy={Boolean(action)}
            onAccept={(edit) => decide('confirm', edit)}
            onConfirmReturn={async (returned) => {
              await onConfirm?.(returned)
            }}
          />
        ) : proposed && canConfirm && !editing ? (
          <>
            <Button
              variant="primary"
              disabled={Boolean(action) || (bulk && bulkSelected.size === 0)}
              onClick={() => void decide('confirm', confirmPayload)}
              title="Confirm (a)"
            >
              Confirm
            </Button>
            {canEdit ? (
              <Button disabled={Boolean(action)} onClick={() => setEditing(true)} title="Edit (e)">
                Edit
              </Button>
            ) : null}
          </>
        ) : proposed && !canConfirm && !blockingErrors.length ? (
          <p className="text-xs text-muted-foreground">View only · your role cannot confirm this card kind.</p>
        ) : null}
        {proposed && canDismiss && !editing && onDismiss ? (
          <DismissControl
            label={report ? 'Not a report' : undefined}
            pending={Boolean(action)}
            onDismiss={(reason) => void decide('dismiss', undefined, undefined, reason)}
          />
        ) : null}
        {decidedActions}
        {visibleCard.kind === 'riley_action' && visibleCard.case_id ? (
          <CaseLinksPopover caseId={visibleCard.case_id} cardId={visibleCard.id} />
        ) : null}
        <div className="ml-auto">
          <RileyFeedback id={visibleCard.id} target="card" onFeedback={onFeedback} />
        </div>
      </div>
    </section>
  )
}
